import React from "react";
import { Phone, MessageCircle, MapPin, CircleCheck as CheckCircle2, ShieldCheck, Sparkles, Clock } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
export default function HeroYasminOvenCleaning() {
  const keyFeatures = [{
    id: "feat-1",
    title: "خدمة منزلية فورية داخل حي الياسمين"
  }, {
    id: "feat-2",
    title: "إزالة أصعب الدهون وتلميع الشعلات والعيون"
  }, {
    id: "feat-3",
    title: "صيانة شاملة للأفران الغازية والكهربائية وقطع غيار أصلية"
  }, {
    id: "feat-4",
    title: "فنيون مختصون مع ضمان سلامة التشغيل ونظافة المكان"
  }];
  return <section id="heroyasminovencleaning" data-nav="light" dir="rtl" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 border-b border-border isolate">
      {/* Background Subtle Accent Pattern */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-40 overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-1/2 -left-28 w-80 h-80 rounded-full bg-secondary/10 blur-2xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Content Column */}
          <div className="lg:col-span-7 flex flex-col items-start text-right">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-medium gap-1.5 rounded-full">
                <MapPin className="size-3.5 text-primary" />
                تغطية معتمدة داخل حي الياسمين
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-xs sm:text-sm font-medium gap-1.5 rounded-full border-border">
                <Sparkles className="size-3.5 text-primary" />
                شركة لمسة احتراف
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.25] mb-5">
              تنظيف أفران حي الياسمين بالرياض وصيانة فورية في منزلك
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6 max-w-2xl">
              تقدم شركة <strong>لمسة احتراف</strong> خدمات متخصصة في تنظيف أفران حي الياسمين وصيانتها مباشرة في منزلك. نقوم بإزالة الدهون المتراكمة، تلميع زجاج الفرن وتنظيف العيون والشعلات، بالإضافة إلى فحص وإصلاح الأعطال للأفران الغازية والكهربائية وتوفير قطع الغيار المعتمدة بأيدي فنيين محترفين.
            </p>

            {/* Quick Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full mb-8">
              {keyFeatures.map((item, index) => <div key={item.id} data-index={index} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-muted/60 border border-border/70">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-foreground">
                    {item.title}
                  </span>
                </div>)}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 shadow-sm gap-2">
                <a href={PHONE_TEL_HREF}>
                  <Phone className="size-4" />
                  <span>اتصال مباشر: {PHONE_LOCAL}</span>
                </a>
              </Button>

              <Button asChild size="lg" variant="outline" className="border-border bg-background hover:bg-accent text-foreground font-semibold px-6 shadow-xs gap-2">
                <Link to={WHATSAPP_HREF} newTab>
                  <MessageCircle className="size-4 text-emerald-600" />
                  <span>محادثة واتساب سريعة</span>
                </Link>
              </Button>
            </div>

            {/* Trust and local reassurance */}
            <div className="mt-8 pt-6 border-t border-border flex flex-wrap items-center gap-6 text-xs sm:text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="size-4 text-primary" />
                <span>ضمان على أعمال الصيانة</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="size-4 text-primary" />
                <span>مواعيد دقيقة وسرعة استجابة</span>
              </div>
              <div className="flex items-center gap-1.5">
                <MapPin className="size-4 text-primary" />
                <span>متواجدون بانتظام في حي الياسمين - شمال الرياض</span>
              </div>
            </div>
          </div>

          {/* Visual Presentation Column */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none rounded-2xl overflow-hidden border border-border bg-card shadow-lg">
              
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/yasmin_oven_cleaning_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/yasmin_oven_cleaning_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/yasmin_oven_cleaning_hero.webp-wvc-sizes" alt="فني صيانة وتنظيف أفران منزلية في حي الياسمين الرياض" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-4 right-4 left-4 text-white">
                  <p className="text-xs font-medium text-white/80">خدمة منزلية معتمدة</p>
                  <p className="text-sm font-semibold">تنظيف وصيانة الأفران المنزلية بأعلى معايير الأمان</p>
                </div>
              </div>

              {/* Informational Card Footer */}
              <div className="p-5 sm:p-6 bg-card space-y-4">
                <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
                  <div>
                    <h2 className="text-sm sm:text-base font-bold text-foreground">
                      فحص فني شامل للفرن
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      كشف تسريبات الغاز، تنظيف وتوسيع الفواني، وتبديل المفاتيح والمفصلات.
                    </p>
                  </div>
                  <Badge variant="default" className="text-xs bg-primary text-primary-foreground shrink-0">
                    حي الياسمين
                  </Badge>
                </div>

                <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground pt-1">
                  <span>منطقة الخدمة: الرياض - حي الياسمين</span>
                  <a href={PHONE_TEL_HREF} className="text-primary font-semibold hover:underline inline-flex items-center gap-1">
                    <span>طلب معاينة</span>
                    <Phone className="size-3" />
                  </a>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}